/**
 * Playtest Arena scale resolution (Option B).
 * Always FIT + CENTER_BOTH against the game's declared logical size.
 * Never pass through RESIZE — that ties logical coords to the React panel.
 */

import { unwrapModuleDefault } from './playtestArenaCompile.js';

/** Fallback when gameConfig has no usable width/height. */
export const PLAYTEST_DEFAULT_WIDTH = 800;
export const PLAYTEST_DEFAULT_HEIGHT = 600;

/** Cap for the Arena mount panel (px). */
export const PLAYTEST_MOUNT_MAX_HEIGHT = 520;

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function positiveInt(value) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.floor(n);
}

/**
 * Unwrap a compiled registry entry (module namespace or bare export).
 * @param {Record<string, unknown>|null|undefined} registry
 * @returns {object|null}
 */
export function readGameConfigFromRegistry(registry) {
  if (!registry || typeof registry !== 'object') return null;
  const raw =
    registry['src/config/gameConfig'] ||
    registry['config/gameConfig'] ||
    registry.gameConfig ||
    null;
  const unwrapped = unwrapModuleDefault(raw);
  if (!unwrapped || typeof unwrapped !== 'object') return null;
  return /** @type {object} */ (unwrapped);
}

/**
 * @param {object|null|undefined} gameConfig
 * @param {number} [fallback=0]
 * @returns {number}
 */
export function readArcadeGravityY(gameConfig, fallback = 0) {
  const y =
    gameConfig &&
    gameConfig.physics &&
    gameConfig.physics.arcade &&
    gameConfig.physics.arcade.gravity &&
    gameConfig.physics.arcade.gravity.y;
  return typeof y === 'number' && Number.isFinite(y) ? y : fallback;
}

/**
 * Resolve Playtest Arena scale from a compiled gameConfig module/object.
 *
 * Option B: always FIT + CENTER_BOTH. Logical size from scale.width/height
 * or top-level width/height; fallback 800×600. Game-requested RESIZE (or
 * any other mode) is ignored for the Arena embed.
 *
 * `parentSize` is accepted for API stability / future use but does not
 * affect logical dimensions under Option B.
 *
 * @param {unknown} gameConfigModule compiled module, bare config, or null
 * @param {{ width?: number, height?: number }|null} [parentSize]
 * @returns {{
 *   mode: 'FIT',
 *   autoCenter: 'CENTER_BOTH',
 *   width: number,
 *   height: number,
 *   aspectRatio: number,
 *   source: 'scale'|'top_level'|'fallback',
 * }}
 */
export function resolvePlaytestScale(gameConfigModule, parentSize = null) {
  void parentSize;
  const config = unwrapModuleDefault(gameConfigModule);
  const obj =
    config && typeof config === 'object'
      ? /** @type {Record<string, unknown>} */ (config)
      : null;

  const scale =
    obj && obj.scale && typeof obj.scale === 'object'
      ? /** @type {Record<string, unknown>} */ (obj.scale)
      : null;

  const fromScaleW = scale ? positiveInt(scale.width) : null;
  const fromScaleH = scale ? positiveInt(scale.height) : null;
  if (fromScaleW != null && fromScaleH != null) {
    return {
      mode: 'FIT',
      autoCenter: 'CENTER_BOTH',
      width: fromScaleW,
      height: fromScaleH,
      aspectRatio: fromScaleW / fromScaleH,
      source: 'scale',
    };
  }

  const topW = obj ? positiveInt(obj.width) : null;
  const topH = obj ? positiveInt(obj.height) : null;
  if (topW != null && topH != null) {
    return {
      mode: 'FIT',
      autoCenter: 'CENTER_BOTH',
      width: topW,
      height: topH,
      aspectRatio: topW / topH,
      source: 'top_level',
    };
  }

  // Partial scale/top-level: fill missing side from defaults.
  const width = fromScaleW ?? topW ?? PLAYTEST_DEFAULT_WIDTH;
  const height = fromScaleH ?? topH ?? PLAYTEST_DEFAULT_HEIGHT;
  const source =
    fromScaleW != null || fromScaleH != null || topW != null || topH != null
      ? fromScaleW != null || fromScaleH != null
        ? 'scale'
        : 'top_level'
      : 'fallback';

  return {
    mode: 'FIT',
    autoCenter: 'CENTER_BOTH',
    width,
    height,
    aspectRatio: width / height,
    source,
  };
}
